import {
  KrarkConfiguration,
  TreeNode,
  ProbabilityTree,
  NodeType,
  FlipResult,
  SpellState,
  AccumulatedResources,
  emptyMana,
  addMana,
  // addResources,
} from 'types';
import {
  calculateTotalTriggers,
  calculateHeadsProbability,
  // calculateOutcomeProbability,
} from './probability';

let nodeIdCounter = 0;

function generateNodeId(): string {
  return `node_${nodeIdCounter++}`;
}

/**
 * Calculate resources generated at a specific step
 */
function calculateResourcesForStep(
  config: KrarkConfiguration,
  isCast: boolean,
  isHeads: boolean,
): AccumulatedResources {
  const resources: AccumulatedResources = {
    cardsDrawn: 0,
    treasuresCreated: 0,
    manaGenerated: emptyMana(),
    copies: 0,
  };

  const { advanced } = config;

  // Apply per-cast resources
  if (isCast) {
    resources.cardsDrawn += advanced.perCast.cardsDrawn;
    resources.treasuresCreated += advanced.perCast.treasuresCreated;
    resources.manaGenerated = addMana(resources.manaGenerated, advanced.perCast.manaGenerated);
  }

  // Apply per-copy resources (only on heads)
  if (isHeads) {
    resources.cardsDrawn += advanced.perCopy.cardsDrawn;
    resources.treasuresCreated += advanced.perCopy.treasuresCreated;
    resources.manaGenerated = addMana(resources.manaGenerated, advanced.perCopy.manaGenerated);
    resources.copies = 1;
  }

  // Apply per-cast-or-copy resources
  resources.cardsDrawn += advanced.perCastOrCopy.cardsDrawn;
  resources.treasuresCreated += advanced.perCastOrCopy.treasuresCreated;
  resources.manaGenerated = addMana(resources.manaGenerated, advanced.perCastOrCopy.manaGenerated);

  return resources;
}

/**
 * Add two AccumulatedResources together
 */
function addAccumulatedResources(
  a: AccumulatedResources,
  b: AccumulatedResources,
): AccumulatedResources {
  return {
    cardsDrawn: a.cardsDrawn + b.cardsDrawn,
    treasuresCreated: a.treasuresCreated + b.treasuresCreated,
    manaGenerated: addMana(a.manaGenerated, b.manaGenerated),
    copies: a.copies + b.copies,
  };
}

/**
 * Build a flip node and its children recursively
 */
function buildFlipNode(
  config: KrarkConfiguration,
  depth: number,
  remainingFlips: number,
  currentState: SpellState,
  currentResources: AccumulatedResources,
  currentProbability: number,
  parentId?: string,
): TreeNode {
  const nodeId = generateNodeId();
  const headsProbability = calculateHeadsProbability(config.krarksThumbs);
  const tailsProbability = 1 - headsProbability;

  // Base flip node
  const flipNode: TreeNode = {
    id: nodeId,
    type: NodeType.FLIP,
    probability: currentProbability,
    parentId,
    children: [],
    state: { ...currentState },
    resources: { ...currentResources },
    depth,
    isTerminal: false,
    label: `Flip ${currentState.totalFlips + 1}`,
    description: `Coin flip ${currentState.totalFlips + 1} of ${calculateTotalTriggers(
      config.krarksInPlay,
      config.harmonicProdigies,
    )}`,
  };

  // Create heads branch
  const headsState: SpellState = {
    ...currentState,
    copies: currentState.copies + 1,
    totalFlips: currentState.totalFlips + 1,
  };

  const headsResources = addAccumulatedResources(
    currentResources,
    calculateResourcesForStep(config, false, true),
  );

  const headsProbabilityTotal = currentProbability * headsProbability;

  const headsNode: TreeNode = {
    id: generateNodeId(),
    type: NodeType.FLIP,
    probability: headsProbabilityTotal,
    flipResult: FlipResult.HEADS,
    parentId: nodeId,
    children: [],
    state: headsState,
    resources: headsResources,
    depth: depth + 1,
    isTerminal: remainingFlips === 1,
    label: 'Heads',
    description: `Copy created! Total copies: ${headsState.copies}`,
  };

  // Create tails branch
  const tailsState: SpellState = {
    ...currentState,
    totalFlips: currentState.totalFlips + 1,
    spellReturned: remainingFlips === 1, // Spell returns if this is the last flip
  };

  const tailsResources = addAccumulatedResources(
    currentResources,
    calculateResourcesForStep(config, false, false),
  );

  const tailsProbabilityTotal = currentProbability * tailsProbability;

  const tailsNode: TreeNode = {
    id: generateNodeId(),
    type: NodeType.FLIP,
    probability: tailsProbabilityTotal,
    flipResult: FlipResult.TAILS,
    parentId: nodeId,
    children: [],
    state: tailsState,
    resources: tailsResources,
    depth: depth + 1,
    isTerminal: remainingFlips === 1,
    label: 'Tails',
    description: remainingFlips === 1 ? 'Spell returns to hand' : 'No copy created',
  };

  // If there are more flips, create children recursively
  if (remainingFlips > 1) {
    headsNode.children = [
      buildFlipNode(
        config,
        depth + 2,
        remainingFlips - 1,
        headsState,
        headsResources,
        headsProbabilityTotal,
        headsNode.id,
      ),
    ];

    tailsNode.children = [
      buildFlipNode(
        config,
        depth + 2,
        remainingFlips - 1,
        tailsState,
        tailsResources,
        tailsProbabilityTotal,
        tailsNode.id,
      ),
    ];
  } else {
    // Terminal nodes - mark as result nodes
    headsNode.type = NodeType.RESULT;
    headsNode.label = 'Result';
    headsNode.description = `Final: ${headsState.copies} copies`;

    tailsNode.type = NodeType.RESULT;
    tailsNode.label = 'Result';
    tailsNode.description = tailsState.spellReturned
      ? `Spell returned, ${tailsState.copies} copies`
      : `Final: ${tailsState.copies} copies`;
  }

  flipNode.children = [headsNode, tailsNode];

  return flipNode;
}

/**
 * Build the complete probability tree for a Krark configuration
 */
export function buildProbabilityTree(config: KrarkConfiguration): ProbabilityTree {
  nodeIdCounter = 0; // Reset counter

  const totalTriggers = calculateTotalTriggers(config.krarksInPlay, config.harmonicProdigies);

  // Start with initial resources from casting
  const initialResources: AccumulatedResources = {
    ...calculateResourcesForStep(config, true, false),
    copies: config.freeCopies,
  };

  const initialState: SpellState = {
    copies: config.freeCopies,
    spellReturned: false,
    totalFlips: 0,
  };

  // Create root node
  const root: TreeNode = {
    id: generateNodeId(),
    type: NodeType.START,
    probability: 1.0,
    children: [],
    state: initialState,
    resources: initialResources,
    depth: 0,
    isTerminal: totalTriggers === 0,
    label: 'Start',
    description: `Cast spell with ${config.freeCopies} free copies`,
  };

  // If there are triggers, build the tree
  if (totalTriggers > 0) {
    root.children = [
      buildFlipNode(config, 1, totalTriggers, initialState, initialResources, 1.0, root.id),
    ];
  } else {
    // No flips, just go straight to result
    root.type = NodeType.RESULT;
    root.isTerminal = true;
  }

  // Collect all terminal nodes and calculate tree stats
  const terminalNodes: TreeNode[] = [];
  let maxDepth = 0;
  let totalNodes = 0;

  function traverseTree(node: TreeNode) {
    totalNodes++;
    maxDepth = Math.max(maxDepth, node.depth);

    if (node.isTerminal) {
      terminalNodes.push(node);
    }

    for (const child of node.children) {
      traverseTree(child);
    }
  }

  traverseTree(root);

  return {
    root,
    totalNodes,
    maxDepth,
    terminalNodes,
  };
}

/**
 * Find a node by ID in the tree
 */
export function findNodeById(root: TreeNode, id: string): TreeNode | null {
  if (root.id === id) return root;

  for (const child of root.children) {
    const found = findNodeById(child, id);
    if (found) return found;
  }

  return null;
}

/**
 * Get all nodes at a specific depth
 */
export function getNodesAtDepth(root: TreeNode, targetDepth: number): TreeNode[] {
  const nodes: TreeNode[] = [];

  function traverse(node: TreeNode) {
    if (node.depth === targetDepth) {
      nodes.push(node);
    }

    for (const child of node.children) {
      traverse(child);
    }
  }

  traverse(root);
  return nodes;
}
